// src/components/PricingTable/index.jsx
import { Box, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import styles from './index.module.css';
import { getPricingRowLabel } from './serviceLabel';

/**
 * NOTE (scoped change for Elite Awards only):
 * We added a prop `forceShowMoney` that, when true, renders the money columns
 * even if `isDecoration` is true. Use it ONLY for the Elite Awards custom
 * supplier↔decorator pairing (supplierId '2147483647' or decoratorId '4').
 * This keeps all other suppliers/POs unchanged.
 *
 * Example usage in PO view:
 *   <PricingTable
 *     ...
 *     isPo
 *     forceShowMoney={
 *       po?.supplierInfo?.supplierId === '4' ||
 *       po?.supplierInfo?.supplierId === '2147483647'
 *     }
 *   />
 */

const formatMoneyWithCode = (value, currency, locale) => {
  if (value == null || value === '') return null;
  const amount = Number(value) || 0;

  if (!currency) return `$${amount.toFixed(2)}`;

  try {
    const formatted = new Intl.NumberFormat(locale || undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return `${formatted} ${currency}`; // e.g. "$123.45 CAD"
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

const toNum = (v) => (v === '' || v == null ? 0 : parseFloat(v)) || 0;
const toQty = (v) => Math.max(1, parseInt(v, 10) || 1);

const PricingTable = ({
  priceDetails,
  isService,
  isPo,
  isArtWork,
  isDecoration,
  currency,
  locale,

  // NEW: Elite-only override to render money columns when needed
  forceShowMoney = false,

  // Optional debug: show the raw row JSON inline (defaults to false)
  showDebugRowJson = false,
  supplierNote = '',
  showPleaseInquireForEmptyPrice = false,
  serviceTaxBreakdown = false,
  lineItemTaxBreakdown = false,
  // Only generated customer invoices use these PO-style SKU/tax columns
  // with customer selling values. Actual supplier POs keep UNIT COST.
  customerSellingPrices = false,
}) => {
  const normalizedSupplierNote = String(supplierNote || '').trim();
  // Precompute display numbers so PO uses netCost and non-PO uses retail/client price
  const rowsComputed = (priceDetails || []).map((row) => {
    const label = String(row.item || '').trim();
    const isTotalRow = label.toLowerCase() === 'total';
    const qty = isTotalRow ? '' : toQty(row.quantity);

    if (isPo && lineItemTaxBreakdown) {
      const unitCost = isTotalRow ? null : toNum(customerSellingPrices
        ? (row.price ?? row.clientPrice ?? row.retail)
        : (row.netCost ?? row.unitCost ?? row.cost ?? row.price));
      const subtotal = isTotalRow
        ? (row.subtotal === '' || row.subtotal == null ? null : toNum(row.subtotal))
        : toNum(row.subtotal ?? (unitCost * toQty(row.quantity)));
      const taxAmount = toNum(row.taxAmount ?? row.tax ?? row.Tax);
      const total = isTotalRow
        ? (row.amount === '' || row.amount == null ? null : toNum(row.amount))
        : toNum(row.amount ?? row.total ?? subtotal + taxAmount);
      const taxLabel = String(row.taxLabel || row.taxName || 'Tax').trim();
      return {
        ...row,
        _isTotalRow: isTotalRow,
        _qty: qty,
        _unit: unitCost,
        _subtotal: subtotal,
        _tax: taxAmount,
        _total: total,
        _taxLabel: taxLabel || 'Tax',
      };
    }

    if (isPo) {
      // PO math: cost-only
      const unitCost = isTotalRow ? null : toNum(row.netCost ?? row.unitCost ?? row.cost ?? row.price);
      const amount = isTotalRow
        ? (row.amount === '' || row.amount == null ? null : toNum(row.amount))
        : unitCost * toQty(row.quantity);
      return { ...row, _isTotalRow: isTotalRow, _qty: qty, _unit: unitCost, _amount: amount };
    }

    if (isService && serviceTaxBreakdown) {
      const subtotal = toNum(row.subtotal ?? row.Subtotal);
      const tax = toNum(row.tax ?? row.Tax);
      const total = toNum(row.total ?? row.Total);
      return { ...row, _isTotalRow: isTotalRow, _qty: qty, _subtotal: subtotal, _tax: tax, _total: total };
    }

    // Non-PO math: use selling price fields
    const unitPrice = isTotalRow ? null : toNum(row.price ?? row.clientPrice ?? row.retail);
    const amount = isTotalRow
      ? (row.amount === '' || row.amount == null ? null : toNum(row.amount))
      : unitPrice * toQty(row.quantity);
    return { ...row, _isTotalRow: isTotalRow, _qty: qty, _unit: unitPrice, _amount: amount };
  });

  const rowsForRender = rowsComputed.filter(
    r => String(r.item || '').trim().toLowerCase() !== 'total units'
  );

  // Render money columns when:
  // - not a decoration table, OR
  // - it is a PO and not the Artwork section, OR
  // - Elite override is explicitly enabled
    const showMoneyCols =
      (!isDecoration) ||
      (isDecoration && isPo && isArtWork) ||
      !!forceShowMoney;

  // MAXIMUS 







  return (
    <>
    <TableContainer component={Paper} className={`${styles.tableContainer} document-table sticky-header`}>
      <Table className={styles.table}>
        <TableHead>
          <TableRow className={styles.tableRow}>
            {isPo && (
              <TableCell className={styles.tableHeaderCell} sx={{ width: '200px !important' }}>
                {isArtWork ? 'Artwork Costs' : 'SKU / Product ID'}
              </TableCell>
            )}
            <TableCell
              className={`${styles.tableHeaderCell} ${styles.firstColumnItem} ${isService ? styles.firstServiceColumnItem : ''} ${isPo ? styles.pofirstServiceColumnItem : ''}`}
            >
              {!isService ? 'ITEM' : 'SERVICE'}
            </TableCell>
            <TableCell className={styles.tableHeaderCell}>QTY</TableCell>
            {showMoneyCols && (
              <>
                {isService && serviceTaxBreakdown ? (
                  <>
                    <TableCell className={styles.tableHeaderCell}>SUBTOTAL</TableCell>
                    <TableCell className={styles.tableHeaderCell}>TAX</TableCell>
                    <TableCell className={styles.tableHeaderCell}>TOTAL</TableCell>
                  </>
                ) : isPo && lineItemTaxBreakdown ? (
                  <>
                    <TableCell className={styles.tableHeaderCell}>{customerSellingPrices ? 'UNIT PRICE' : 'UNIT COST'}</TableCell>
                    <TableCell className={styles.tableHeaderCell}>SUBTOTAL</TableCell>
                    <TableCell className={styles.tableHeaderCell}>TAX</TableCell>
                    <TableCell className={styles.tableHeaderCell}>TOTAL</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className={styles.tableHeaderCell}>
                      {isPo ? 'UNIT COST' : 'PRICE'}
                    </TableCell>
                    <TableCell className={styles.tableHeaderCell}>AMOUNT</TableCell>
                  </>
                )}
              </>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {rowsForRender.map((row, index) => {
            // Suppress $0.00 lines specifically under Artwork Costs when both unit and amount are zero/empty
            const suppressArtworkMoney =
              !!isArtWork &&
              !row._isTotalRow &&
              ((row._unit == null || Number(row._unit) === 0) &&
               (row._amount == null || Number(row._amount) === 0));
            const showPleaseInquire =
              !!showPleaseInquireForEmptyPrice &&
              !isPo &&
              !row._isTotalRow &&
              (row.price === '' || row.price == null) &&
              (row.amount === '' || row.amount == null);
            const suppressArtworkBreakdownMoney =
              !!isArtWork &&
              !!lineItemTaxBreakdown &&
              !row._isTotalRow &&
              Number(row._subtotal || 0) === 0 &&
              Number(row._tax || 0) === 0 &&
              Number(row._total || 0) === 0;

            return (
              <TableRow key={index} className={styles.tableRow}>
                {isPo && (
                  <TableCell className={`${styles.tableCell} ${row.item === 'Total Units' ? styles.unitRow : ''}`}>
                    <Box style={{ width: '100%' }} className={row.item === 'Total' || row.item === 'Total Units' ? styles.total : ''}>
                      {isArtWork ? row.decoration : ((row.sku ?? row.SKU ?? '').trim() || row.id)}
                    </Box>
                  </TableCell>
                )}

                <TableCell
                  className={`${styles.tableCell} ${styles.firstColumnItem} ${row.item === 'Total Units' ? styles.unitRow : ''} ${isService ? styles.firstServiceColumnItem : ''}`}
                >
                  <Box style={{ width: '100%' }} className={row.item === 'Total' || row.item === 'Total Units' ? styles.total : ''}>
                    {getPricingRowLabel(row, isService)}
                  </Box>
                </TableCell>

                <TableCell className={`${styles.tableCell} ${row.item === 'Total Units' ? styles.unitRow : ''}`}>
                  <Box style={{ width: '100%' }} className={row.item === 'Total Units' ? styles.total : ''}>
                    {row._isTotalRow ? '' : row._qty}
                  </Box>
                </TableCell>

                {showDebugRowJson && (
                  <TableCell className={styles.tableCell} colSpan={showMoneyCols ? 3 : 1}>
                    <Box style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}>
                      {JSON.stringify(row)}
                    </Box>
                  </TableCell>
                )}

                {!showDebugRowJson && showMoneyCols && (
                  <>
                    {isService && serviceTaxBreakdown ? (
                      <>
                        <TableCell className={styles.tableCell}>
                          <Box style={{ width: '100%' }}>
                            {formatMoneyWithCode(row._subtotal, currency, locale)}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCell}>
                          <Box style={{ width: '100%' }}>
                            {formatMoneyWithCode(row._tax, currency, locale)}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCell}>
                          <Box className={row.item === 'Total' ? styles.total : ''} style={{ width: '100%' }}>
                            {formatMoneyWithCode(row._total, currency, locale)}
                          </Box>
                        </TableCell>
                      </>
                    ) : isPo && lineItemTaxBreakdown ? (
                      <>
                        <TableCell className={styles.tableCell}>
                          <Box style={{ width: '100%' }}>
                            {suppressArtworkBreakdownMoney ? null : formatMoneyWithCode(row._unit, currency, locale)}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCell}>
                          <Box style={{ width: '100%' }}>
                            {suppressArtworkBreakdownMoney ? null : formatMoneyWithCode(row._subtotal, currency, locale)}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCell}>
                          <Box style={{ width: '100%' }}>
                            {suppressArtworkBreakdownMoney ? null : (
                              <>
                                <div>{row._taxLabel}</div>
                                <div>{formatMoneyWithCode(row._tax, currency, locale)}</div>
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCell}>
                          <Box className={row.item === 'Total' ? styles.total : ''} style={{ width: '100%' }}>
                            {suppressArtworkBreakdownMoney
                              ? (row.includedInPrice ? 'Included in unit price' : null)
                              : formatMoneyWithCode(row._total, currency, locale)}
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className={styles.tableCell}>
                          <Box style={{ width: '100%' }}>
                            {showPleaseInquire
                              ? 'Please inquire'
                              : (suppressArtworkMoney ? null : formatMoneyWithCode(row._unit, currency, locale))}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCell}>
                          <Box className={row.item === 'Total' ? styles.total : ''} style={{ width: '100%' }}>
                            {showPleaseInquire
                              ? 'Please inquire'
                              : (suppressArtworkMoney ? null : formatMoneyWithCode(row._amount, currency, locale))}
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    {normalizedSupplierNote && (
      <p className={styles.supplierNote}>
        <strong className={styles.supplierNoteLabel}>Shipping note:</strong> {normalizedSupplierNote}
      </p>
    )}
    </>
  );
};

export default PricingTable;
